import React, { useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useOnboarding } from './useOnboarding';
import { clientTourSteps, practitionerTourSteps, practitionerStepGroupMap, clientStepGroupMap } from './tourSteps';
import { useAuth } from '@/contexts/AuthContext';

interface OnboardingTourProps {
  userType: 'client' | 'practitioner';
}

export const OnboardingTour: React.FC<OnboardingTourProps> = ({ userType }) => {
  const { user } = useAuth();
  const {
    isRunning,
    setIsRunning,
    stepIndex,
    setStepIndex,
    endTour,
  } = useOnboarding(userType, user?.id);

  const steps = userType === 'client' ? clientTourSteps : practitionerTourSteps;

  // Auto-expand sidebar groups before showing a step (works for both client and practitioner)
  const expandGroupForStep = useCallback((nextStepIndex: number) => {
    const step = steps[nextStepIndex];
    if (!step) return;

    const target = typeof step.target === 'string' ? step.target : '';
    const groupMap = userType === 'practitioner' ? practitionerStepGroupMap : clientStepGroupMap;
    const groupName = groupMap[target];
    if (groupName) {
      window.dispatchEvent(new CustomEvent('tour-expand-group', { detail: { group: groupName } }));
    }
  }, [userType, steps]);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;

    // Handle tour completion or skip
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      endTour();
      return;
    }

    // Handle step navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        const nextIndex = index + 1;
        expandGroupForStep(nextIndex);
        // Small delay to let the group expand before Joyride looks for the target
        setTimeout(() => setStepIndex(nextIndex), 150);
        return;
      } else if (action === ACTIONS.PREV) {
        const prevIndex = index - 1;
        expandGroupForStep(prevIndex);
        setTimeout(() => setStepIndex(prevIndex), 150);
        return;
      }
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      endTour();
    }
  }, [endTour, setStepIndex, expandGroupForStep]);

  // Expand group for the first step on mount
  React.useEffect(() => {
    if (isRunning && stepIndex === 0) {
      expandGroupForStep(0);
    }
  }, [isRunning, stepIndex, expandGroupForStep]);

  return (
    <Joyride
      steps={steps}
      run={isRunning}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      spotlightClicks
      disableOverlayClose
      callback={handleJoyrideCallback}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      styles={{
        options: {
          primaryColor: '#D97706',
          textColor: '#1f2937',
          backgroundColor: '#ffffff',
          arrowColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 8,
          padding: 16,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          fontSize: 16,
          fontWeight: 600,
          marginBottom: 8,
        },
        tooltipContent: {
          fontSize: 14,
          lineHeight: 1.5,
        },
        buttonNext: {
          backgroundColor: '#D97706',
          borderRadius: 6,
          padding: '8px 16px',
          fontSize: 14,
          fontWeight: 500,
        },
        buttonBack: {
          color: '#6b7280',
          marginRight: 8,
        },
        buttonSkip: {
          color: '#9ca3af',
          fontSize: 13,
        },
        spotlight: {
          borderRadius: 8,
        },
        beacon: {
          display: 'none',
        },
      }}
    />
  );
};

export default OnboardingTour;
